import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  CalendarClock,
  Check,
  Copy,
  Download,
  FileCheck2,
  FileText,
  History,
  Link as LinkIcon,
  Mail,
  MapPin,
  MoreHorizontal,
  ReceiptText,
  RotateCcw,
  Trash2,
  Phone,
  UserRound,
  XCircle,
} from 'lucide-react';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import {
  anularFactura,
  discardFacturaDraft,
  downloadFacturaPdf,
  fetchFacturaDetail,
  previewFacturaPdf,
  sendFacturaEmail,
} from '@/services/facturacion.js';
import AlertDialog from '@/components/ui/alert-dialog.jsx';
import RecuperarFacturaAfipDialog from './RecuperarFacturaAfipDialog.jsx';
import Badge from '@/components/ui/badge.jsx';
import Button from '@/components/ui/button.jsx';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu.jsx';
import Tooltip from '@/components/ui/tooltip.jsx';
import DetailPageHeader from '@/components/layout/DetailPageHeader.jsx';
import DetailSummaryGrid, { DetailSummaryItem } from '@/components/layout/DetailSummaryGrid.jsx';
import { buildGlobalDiscountRows } from '@/services/invoices.js';
import CollapsibleDetailSection from '@/components/layout/CollapsibleDetailSection.jsx';
import CobranzaDesdeFacturaModal from '@/pages/Tesoreria/Cobranzas/CobranzaDesdeFacturaModal.jsx';
import {
  dateKey,
  deriveFinancialStatus,
  displayDate,
  FINANCIAL_EPSILON,
  hasInvalidDueDateOrder,
} from './facturaDetalle.utils.js';

const money = new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' });

const formatMoney = (value) => money.format(Number(value || 0));
const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';
const formatCuit = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 11 ? `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}` : value;
};
const splitEmails = (value) => String(value || '').split(/[;,\s]+/).map((email) => email.trim()).filter(Boolean);
const validEmails = (value) => splitEmails(value).every((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

const formatDate = (value, time = false) => {
  if (!value) return 'Sin dato';
  const key = dateKey(value);
  if (!time && key) return displayDate(key);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin dato';
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(time ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date);
};

const typeLabel = (fiscal = {}) => {
  const type = String(fiscal.tipoComprobante || '').toUpperCase();
  return type.includes('CRED') || type.startsWith('NC')
    ? `Nota de crédito ${fiscal.letra || ''}`.trim()
    : `Factura ${fiscal.letra || ''}`.trim();
};

const fiscalStatus = (factura, fiscal) => {
  const value = String(factura?.estado || '').toUpperCase();
  if (value.includes('ANUL')) return 'Anulada';
  if (value.includes('RECH')) return 'Rechazada';
  if (value.includes('BORR') || value.includes('PEND')) return 'Pendiente de autorización';
  return fiscal?.cae || value.includes('EMIT') || value.includes('AUTORIZ')
    ? 'Autorizada'
    : 'Pendiente de autorización';
};

const documentNumber = (factura, fiscal) => {
  const raw = String(factura?.numero || '').trim();
  const [rawPoint, rawNumber] = raw.includes('-') ? raw.split('-') : ['', raw];
  const point = String(rawPoint || fiscal?.puntoVenta || '').replace(/\D/g, '');
  const number = String(rawNumber || '').replace(/\D/g, '');
  if (!number) return 'Sin número';
  return `${point ? point.padStart(4, '0') : '0000'}-${number.padStart(8, '0')}`;
};

const statusConfig = {
  Autorizada: {
    variant: 'default',
    className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  },
  Pagada: {
    variant: 'default',
    className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  },
  Parcial: {
    variant: 'warning',
    className: 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300',
  },
  Vencida: {
    variant: 'danger',
    className: 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300',
  },
  Rechazada: {
    variant: 'danger',
    className: 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300',
  },
  Anulada: {
    variant: 'outline',
    className: 'border-[rgba(var(--border),0.72)] bg-[rgba(var(--subpanel),0.62)] text-[rgba(var(--muted),0.96)]',
  },
  Pendiente: {
    variant: 'outline',
    className: 'border-[rgba(var(--border),0.72)] bg-[rgba(var(--subpanel),0.48)] text-[rgb(var(--fg))]',
  },
  'Pendiente de autorización': {
    variant: 'warning',
    className: 'border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-300',
  },
};

function StatusBadge({ children }) {
  const config = statusConfig[children] || statusConfig.Pendiente;
  return (
    <Badge
      variant={config.variant}
      className={`normal-case tracking-normal ${config.className}`}
    >
      {children}
    </Badge>
  );
}

function Panel({ title, children, className = '', action }) {
  return (
    <section className={`rounded-xl border border-[rgba(var(--border),.55)] bg-[rgba(var(--panel),.92)] p-4 shadow-sm ${className}`}>
      <div className="flex items-center justify-between gap-3"><h2 className="text-base font-semibold text-[rgb(var(--fg))]">{title}</h2>{action}</div>
      {children}
    </section>
  );
}

function FiscalPanel({ summary, children, className = '' }) {
  return (
    <CollapsibleDetailSection bare className={className} summary={summary} title="Información fiscal e impositiva">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 text-base font-semibold text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
        <span>Información fiscal e impositiva</span>
        {summary ? <span className="text-xs font-medium text-[rgba(var(--muted),.98)]">{summary}</span> : null}
      </summary>
      <div className="border-t border-[rgba(var(--border),.4)] p-4">{children}</div>
    </CollapsibleDetailSection>
  );
}

function DataList({ rows }) {
  return (
    <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
      {rows.filter((row) => row.visible !== false).map((row) => (
        <div
          key={row.label}
          className="flex items-start justify-between gap-3 border-b border-[rgba(var(--border),.28)] py-1"
        >
          <dt className="text-[rgba(var(--fg),.6)]">{row.label}</dt>
          <dd className="text-right font-medium text-[rgb(var(--fg))]">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function FacturaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenant, token, can, user } = useAuth();
  const historyRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [copied, setCopied] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState('');
  const [emailForm, setEmailForm] = useState({ to: '', cc: '', subject: '', message: '' });
  const [voidReason, setVoidReason] = useState('');
  const [voidNotes, setVoidNotes] = useState('');
  const [cobranzaModalOpen, setCobranzaModalOpen] = useState(false);
  const [discardDraftDialogOpen, setDiscardDraftDialogOpen] = useState(false);
  const [afipRecoveryOpen, setAfipRecoveryOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const result = await fetchFacturaDetail({ tenant, token, id });
        if (active) setData(result);
      } catch (requestError) {
        if (active) setError(requestError.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id, tenant, token]);

  const factura = data?.factura;
  const fiscal = data?.fiscal || {};
  const cobranza = data?.cobranza || {};
  const cliente = data?.cliente || {};
  const redondeoFiscal = data?.impuestos?.redondeoFiscal || null;
  const estadoFiscal = fiscalStatus(factura, fiscal);
  const saldo = Number(cobranza.saldo ?? factura?.saldo_pendiente ?? factura?.saldo ?? factura?.total ?? 0);
  const totalPagos = Number(cobranza.totalPagosAplicados || 0);
  const estadoCobro = deriveFinancialStatus({
    saldo,
    totalPagosAplicados: totalPagos,
    fechaVencimiento: cobranza.fechaVencimiento,
  });
  const clientName = cliente.razonSocial || cliente.nombreFantasia || cliente.nombre || 'Sin dato';
  const invoiceLabel = `${typeLabel(fiscal)} ${documentNumber(factura, fiscal)}`;
  const issueDate = fiscal.fechaComprobante || fiscal.fechaHoraEmision;
  const dueDate = cobranza.fechaVencimiento;
  const invalidDateOrder = hasInvalidDueDateOrder({
    fechaEmision: issueDate,
    fechaVencimiento: dueDate,
  });
  const pointOfSale = fiscal.puntoVenta == null
    ? 'Sin dato'
    : String(fiscal.puntoVenta).padStart(4, '0');
  const isCreditNote = String(fiscal.tipoComprobante || '').toUpperCase().startsWith('NC')
    || String(fiscal.tipoComprobante || '').toUpperCase().includes('CRED');
  const isAnnulled = String(factura?.estado || '').toUpperCase().includes('ANUL');
  const isDraft = String(factura?.estado || '').toUpperCase().includes('BORR');
  const permissions = data?.permisos || {};
  const canRegisterCollection = !isDraft
    && estadoFiscal === 'Autorizada'
    && saldo > FINANCIAL_EPSILON
    && (permissions.canRegisterPayment ?? can('COBRANZAS_REGISTRAR'));
  const canViewPdf = !isDraft && can('FACTURACION_REIMPRIMIR');
  const canIssueCreditNote = (permissions.canVoid ?? can('FACTURACION_ANULAR'))
    && estadoFiscal === 'Autorizada'
    && !isCreditNote
    && !isAnnulled
    && totalPagos <= FINANCIAL_EPSILON
    && !cobranza.notasCredito?.length;
  useEffect(() => {
    if (searchParams.get('accion') !== 'cobranza' || !canRegisterCollection) return;
    setCobranzaModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('accion');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, canRegisterCollection]);
  const canSendEmail = !isDraft && (permissions.canSendEmail ?? Boolean(cliente.email));
  const canViewAccountStatement = permissions.canViewAccountStatement ?? Boolean(cliente.id);
  const taxRows = useMemo(() => {
    const taxes = data?.impuestos || {};
    const discount = taxes.descuentoGlobal;
    const rows = [
      ...(discount ? buildGlobalDiscountRows({ net: taxes.netoGravado, discount: discount.importe, percent: discount.porcentaje }) : []),
      ['Neto gravado', taxes.netoGravado], ['Neto no gravado', taxes.netoNoGravado], ['Exento', taxes.exento],
    ];
    Object.entries(taxes.alicuotas || {}).forEach(([rate, amount]) => rows.push([`IVA ${String(rate).replace('.', ',')} %`, amount]));
    return rows.concat([['Percepciones', taxes.percepciones], ['Retenciones', taxes.retenciones], ['Otros tributos', taxes.tributos]])
      .filter(([, value]) => Number(value || 0) !== 0);
  }, [data?.impuestos]);

  const rememberCopied = (key) => {
    setCopied(key);
    window.setTimeout(() => setCopied(''), 1800);
  };

  const copyText = async (value, key) => {
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const input = document.createElement('textarea');
        input.value = value;
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }
      rememberCopied(key);
    } catch {
      setActionError('No se pudo copiar la información al portapapeles.');
    }
  };

  const previewPdf = async () => {
    setActionError('');
    setPdfLoading(true);
    try {
      await previewFacturaPdf({ tenant, token, id: factura.id });
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadPdf = async () => {
    setActionError('');
    setDownloadLoading(true);
    try {
      await downloadFacturaPdf({
        tenant,
        token,
        id: factura.id,
        fileName: `${invoiceLabel.replace(/\s+/g, '-').toLowerCase()}.pdf`,
      });
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setDownloadLoading(false);
    }
  };

  const openEmailDialog = () => {
    setEmailFeedback('');
    setEmailForm({ to: cliente.email || '', cc: '', subject: invoiceLabel, message: `Hola,\n\nAdjuntamos el comprobante ${invoiceLabel}.\n\nSaludos.` });
    setEmailDialogOpen(true);
  };

  const sendInvoice = async () => {
    if (!splitEmails(emailForm.to).length || !validEmails(emailForm.to) || (emailForm.cc && !validEmails(emailForm.cc))) {
      setEmailFeedback('Ingresá destinatarios válidos, separados por coma, punto y coma o espacio.');
      return false;
    }
    setEmailFeedback('');
    try {
      await sendFacturaEmail({ tenant, token, id: factura.id, ...emailForm });
      setEmailFeedback('Factura enviada correctamente.');
      setEmailDialogOpen(false);
    } catch (requestError) {
      setEmailFeedback(requestError.message);
      return false;
    } finally { /* AlertDialog manages the pending state. */ }
    return true;
  };

  const showHistory = () => {
    if (!historyRef.current) return;
    historyRef.current.open = true;
    historyRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.requestAnimationFrame(() => historyRef.current?.querySelector('summary')?.focus());
  };

  const issueCreditNote = async () => {
    setActionError('');
    try {
      if (!voidReason || !voidNotes.trim()) {
        setActionError('Indicá el motivo y las observaciones de la anulación.');
        return false;
      }
      const result = await anularFactura({ tenant, token, id: factura.id, motivo: voidReason, observaciones: voidNotes.trim() });
      navigate(`/facturacion/${result.notaCreditoId}`);
    } catch (requestError) {
      setActionError(requestError.message);
      throw requestError;
    }
  };

  // Level-1000 users can record a draft that AFIP authorized but was not stored.
  const canRecoverFromAfip = Number(user?.nivel || 0) >= 1000;
  const reloadAfterRecovery = async () => {
    setActionError('');
    try {
      setData(await fetchFacturaDetail({ tenant, token, id }));
    } catch (requestError) {
      setActionError(requestError.message || 'La factura se recuperó, pero no se pudo recargar el detalle.');
    }
  };

  const resumeDraft = () => {
    navigate('/facturacion/manual/nueva', { state: { draft: data } });
  };

  const discardDraft = async () => {
    setActionError('');
    try {
      await discardFacturaDraft({ tenant, token, id: factura.id });
      navigate('/facturacion');
      return true;
    } catch (requestError) {
      setActionError(requestError.message || 'No se pudo eliminar el borrador.');
      throw requestError;
    }
  };

  if (loading) return <div className="mx-auto max-w-[1180px] p-4 text-sm">Cargando factura...</div>;
  if (error) return <div className="mx-auto max-w-[1180px] p-4 text-sm text-red-700">{error}</div>;
  if (!factura) return null;

  return (
    <div className="mx-auto w-full max-w-[1600px] px-3 py-3 sm:px-4 lg:px-[clamp(12px,2vw,28px)]">
      <DetailPageHeader title={null}>
        <div className="flex min-w-0 flex-wrap items-start gap-x-3 gap-y-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate('/facturacion')}
            className="h-8 shrink-0 px-2.5 text-sm shadow-none active:translate-y-px"
          >
            <ArrowLeft aria-hidden="true" className="h-4 w-4" />
            Volver
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h1 className="min-w-0 text-xl font-semibold tracking-tight text-[rgb(var(--fg))] sm:text-2xl">
                {invoiceLabel}
              </h1>
              <StatusBadge>{estadoFiscal}</StatusBadge>
              <StatusBadge>{estadoCobro}</StatusBadge>
            </div>
          </div>
        </div>

        <div className="mt-2 grid min-w-0 gap-2 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
          <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[rgba(var(--muted),0.98)]">
            <span className="inline-flex items-center gap-1.5">
              <CalendarClock aria-hidden="true" className="h-4 w-4 text-[rgb(var(--brand))]" />
              Emitida el {formatDate(fiscal.fechaHoraEmision || issueDate, true)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin aria-hidden="true" className="h-4 w-4 text-[rgb(var(--brand))]" />
              Punto de venta {pointOfSale}
            </span>
            <span className="hidden min-w-0 items-center gap-1.5 xl:inline-flex">
              <UserRound aria-hidden="true" className="h-4 w-4 shrink-0 text-[rgb(var(--brand))]" />
              <span className="max-w-56 truncate">{clientName}</span>
            </span>
            {dueDate ? (
              <span className={estadoCobro === 'Vencida'
                ? 'font-medium text-red-700 dark:text-red-300'
                : 'text-[rgba(var(--muted),0.98)]'}
              >
                {estadoCobro === 'Vencida' ? 'Venció' : 'Vence'} el {displayDate(dueDate)}
              </span>
            ) : null}
          </div>

          <div className="flex min-w-0 flex-wrap items-center gap-2 lg:justify-end">
            {isDraft ? (
              <>
                <Button
                  type="button"
                  variant="primary"
                  onClick={resumeDraft}
                  className="h-8 shrink-0 px-3 text-sm shadow-sm hover:shadow-sm active:translate-y-px"
                >
                  <FileCheck2 aria-hidden="true" className="h-4 w-4" />
                  Emitir definitivamente
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDiscardDraftDialogOpen(true)}
                  className="h-8 shrink-0 px-3 text-sm text-red-700 shadow-none hover:bg-red-500/10 active:translate-y-px"
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                  Eliminar borrador
                </Button>
                {canRecoverFromAfip ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAfipRecoveryOpen(true)}
                    className="h-8 shrink-0 px-3 text-sm shadow-none active:translate-y-px"
                  >
                    <RotateCcw aria-hidden="true" className="h-4 w-4" />
                    Recuperar desde AFIP
                  </Button>
                ) : null}
              </>
            ) : null}
            {canRegisterCollection ? (
              <Button
                type="button"
                variant="primary"
                onClick={() => setCobranzaModalOpen(true)}
                className="h-8 shrink-0 px-3 text-sm shadow-sm hover:shadow-sm active:translate-y-px"
              >
                <ReceiptText aria-hidden="true" className="h-4 w-4" />
                Registrar cobranza
              </Button>
            ) : null}
            {canViewPdf ? (
              <Button
                type="button"
                variant="outline"
                onClick={previewPdf}
                disabled={pdfLoading}
                aria-busy={pdfLoading}
                className="h-8 px-3 text-sm shadow-none active:translate-y-px"
              >
                <FileText aria-hidden="true" className="h-4 w-4" />
                {pdfLoading ? 'Abriendo...' : 'Ver PDF'}
              </Button>
            ) : null}
            <DropdownMenu
              label="Más acciones de la factura"
              trigger={(
                <Tooltip content="Más acciones" side="top">
                  <Button
                    type="button"
                    variant="outline"
                    aria-label="Más acciones"
                    className="h-8 px-3 text-sm shadow-none active:translate-y-px"
                  >
                    <MoreHorizontal aria-hidden="true" className="h-4 w-4" />
                    Más acciones
                  </Button>
                </Tooltip>
              )}
            >
              {({ close }) => (
                <>
                  {canViewPdf ? (
                    <DropdownMenuItem
                      icon={FileText}
                      className="sm:hidden"
                      onSelect={() => {
                        close();
                        previewPdf();
                      }}
                    >
                      Ver PDF
                    </DropdownMenuItem>
                  ) : null}
                  {canSendEmail ? (
                    <DropdownMenuItem
                      icon={Mail}
                      onSelect={() => {
                        close();
                        openEmailDialog();
                      }}
                    >
                      Enviar por correo
                    </DropdownMenuItem>
                  ) : null}
                  {canViewPdf ? (
                    <DropdownMenuItem
                      icon={Download}
                      disabled={downloadLoading}
                      onSelect={() => {
                        close();
                        downloadPdf();
                      }}
                    >
                      {downloadLoading ? 'Descargando...' : 'Descargar PDF'}
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem
                    icon={copied === 'cae' ? Check : Copy}
                    disabled={!fiscal.cae}
                    onSelect={() => {
                      close();
                      copyText(fiscal.cae, 'cae');
                    }}
                  >
                    {copied === 'cae' ? 'CAE copiado' : 'Copiar CAE'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    icon={copied === 'link' ? Check : LinkIcon}
                    onSelect={() => {
                      close();
                      copyText(window.location.href, 'link');
                    }}
                  >
                    {copied === 'link' ? 'Enlace copiado' : 'Copiar enlace'}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    icon={History}
                    onSelect={() => {
                      close();
                      showHistory();
                    }}
                  >
                    Ver historial
                  </DropdownMenuItem>
                  {canIssueCreditNote ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        icon={XCircle}
                        danger
                        onSelect={() => {
                          close();
                          setCreditDialogOpen(true);
                        }}
                      >
                        Anular comprobante
                      </DropdownMenuItem>
                    </>
                  ) : null}
                </>
              )}
            </DropdownMenu>
          </div>
        </div>

        {invalidDateOrder ? (
          <div
            role="status"
            className="mt-3 flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-300"
          >
            <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            La fecha de vencimiento es anterior a la fecha de emisión. Revisá los datos del comprobante.
          </div>
        ) : null}
      </DetailPageHeader>
      {!isDraft ? <CobranzaDesdeFacturaModal
        open={cobranzaModalOpen}
        factura={factura}
        cliente={cliente}
        fiscal={fiscal}
        saldo={saldo}
        onClose={() => setCobranzaModalOpen(false)}
        onRegistered={async () => {
          const result = await fetchFacturaDetail({ tenant, token, id });
          setData(result);
        }}
      /> : null}

      {actionError ? (
        <p
          role="alert"
          className="mt-3 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {actionError}
        </p>
      ) : null}

      <AlertDialog
        open={creditDialogOpen}
        onOpenChange={setCreditDialogOpen}
        title="Anular comprobante"
        description={`Esta acción no eliminará la factura. Se generará una Nota de Crédito vinculada a ${invoiceLabel} y se recalculará el saldo del cliente.`}
        confirmLabel="Continuar con la anulación"
        pendingLabel="Emitiendo..."
        variant="danger"
        onConfirm={issueCreditNote}
      >
        <dl className="grid gap-1 text-sm text-[rgba(var(--muted),.96)]"><div className="flex justify-between gap-4"><dt>Cliente</dt><dd>{clientName}</dd></div><div className="flex justify-between gap-4"><dt>Fecha</dt><dd>{formatDate(issueDate)}</dd></div><div className="flex justify-between gap-4"><dt>Total</dt><dd>{formatMoney(factura.total)}</dd></div><div className="flex justify-between gap-4"><dt>Saldo</dt><dd>{formatMoney(saldo)}</dd></div></dl>
        <label className="mt-4 block text-sm font-medium">Motivo<select value={voidReason} onChange={(event) => setVoidReason(event.target.value)} className="mt-1 w-full rounded-lg border border-[rgba(var(--border),.7)] bg-transparent p-2"><option value="">Seleccionar motivo</option><option value="Error de emisión">Error de emisión</option><option value="Operación cancelada">Operación cancelada</option><option value="Datos fiscales incorrectos">Datos fiscales incorrectos</option><option value="Otro">Otro</option></select></label>
        <label className="mt-3 block text-sm font-medium">Observaciones<textarea required value={voidNotes} onChange={(event) => setVoidNotes(event.target.value)} className="mt-1 min-h-20 w-full rounded-lg border border-[rgba(var(--border),.7)] bg-transparent p-2" /></label>
      </AlertDialog>

      <AlertDialog
        open={discardDraftDialogOpen}
        onOpenChange={setDiscardDraftDialogOpen}
        title="Eliminar borrador"
        description={`Vas a eliminar el borrador ${invoiceLabel}. Esta acción no emite ningún comprobante y no se puede deshacer.`}
        confirmLabel="Eliminar borrador"
        pendingLabel="Eliminando..."
        variant="danger"
        onConfirm={discardDraft}
      />

      {isDraft && canRecoverFromAfip ? (
        <RecuperarFacturaAfipDialog
          open={afipRecoveryOpen}
          onOpenChange={setAfipRecoveryOpen}
          facturaId={factura.id}
          tenant={tenant}
          token={token}
          onRecovered={reloadAfterRecovery}
        />
      ) : null}

      <AlertDialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen} title="Enviar factura por correo" description="Se adjuntará el PDF del comprobante." confirmLabel="Enviar factura" pendingLabel="Enviando..." onConfirm={sendInvoice}>
        <label className="block text-sm font-medium">Destinatarios<input value={emailForm.to} onChange={(event) => setEmailForm({ ...emailForm, to: event.target.value })} className="mt-1 w-full rounded-lg border border-[rgba(var(--border),.7)] bg-transparent p-2" placeholder="correo@empresa.com" /></label>
        <label className="mt-3 block text-sm font-medium">CC (opcional)<input value={emailForm.cc} onChange={(event) => setEmailForm({ ...emailForm, cc: event.target.value })} className="mt-1 w-full rounded-lg border border-[rgba(var(--border),.7)] bg-transparent p-2" /></label>
        <label className="mt-3 block text-sm font-medium">Asunto<input value={emailForm.subject} onChange={(event) => setEmailForm({ ...emailForm, subject: event.target.value })} className="mt-1 w-full rounded-lg border border-[rgba(var(--border),.7)] bg-transparent p-2" /></label>
        <label className="mt-3 block text-sm font-medium">Mensaje<textarea value={emailForm.message} onChange={(event) => setEmailForm({ ...emailForm, message: event.target.value })} className="mt-1 min-h-20 w-full rounded-lg border border-[rgba(var(--border),.7)] bg-transparent p-2" /></label>
        {emailFeedback ? <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-300">{emailFeedback}</p> : null}
      </AlertDialog>

      <main className="flex flex-col gap-4 pt-3">
        <DetailSummaryGrid>
          <DetailSummaryItem label="Cliente" value={clientName} detail={[cliente.cuit ? `CUIT ${formatCuit(cliente.cuit)}` : null, cliente.condicionIVA].filter(Boolean).join(' · ')} />
          <DetailSummaryItem label="Estado" value={<StatusBadge>{estadoFiscal}</StatusBadge>} detail={estadoCobro === 'Pendiente' ? 'Saldo pendiente' : estadoCobro} />
          <DetailSummaryItem label="Emisión / vencimiento" value={`${formatDate(issueDate)} · ${dueDate ? displayDate(dueDate) : 'Sin vencimiento'}`} detail={formatDate(fiscal.fechaHoraEmision || issueDate, true)} />
          <DetailSummaryItem label="Total / saldo" value={formatMoney(cobranza.totalOriginal ?? factura.total)} detail={<span className="font-semibold text-[rgb(var(--brand))]">Saldo {formatMoney(saldo)}</span>} />
        </DetailSummaryGrid>
        <section className="hidden">
          <Panel title="Cliente" action={canViewAccountStatement ? <Button type="button" variant="outline" onClick={() => navigate(`/clientes/${cliente.id}/cuenta-corriente`)} className="h-8 px-3 text-xs">Ver estado de cuenta</Button> : null}>
            <p className="mt-2 font-medium">{clientName}</p>
            {cliente.nombreFantasia && cliente.nombreFantasia !== clientName ? <p className="text-sm text-[rgba(var(--fg),.62)]">{cliente.nombreFantasia}</p> : null}
            <div className="mt-3 grid gap-x-5 gap-y-2 text-sm sm:grid-cols-2">
              {cliente.cuit ? <span>CUIT {formatCuit(cliente.cuit)}</span> : null}
              {cliente.condicionIVA ? <span>{cliente.condicionIVA}</span> : null}
              {cliente.condicionVenta ? <span>{cliente.condicionVenta}</span> : null}
              {cliente.domicilio ? <span className="inline-flex gap-1.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--brand))]" />{[cliente.domicilio, cliente.localidad, cliente.provincia].filter(hasValue).join(', ')}</span> : null}
              {cliente.email ? <span className="inline-flex gap-1.5"><Mail className="h-4 w-4 shrink-0 text-[rgb(var(--brand))]" />{cliente.email}</span> : null}
              {cliente.telefono ? <span className="inline-flex gap-1.5"><Phone className="h-4 w-4 shrink-0 text-[rgb(var(--brand))]" />{cliente.telefono}</span> : null}
              {cliente.sucursalResponsable ? <span className="inline-flex gap-1.5"><Building2 className="h-4 w-4 shrink-0 text-[rgb(var(--brand))]" />{cliente.sucursalResponsable}</span> : null}
            </div>
            {cliente.codigo ? <p className="mt-2 text-xs text-[rgba(var(--muted),.9)]">Código cliente: {cliente.codigo}</p> : null}
          </Panel>
          <Panel title="Cobranza y saldo" className="border-[rgba(var(--brand),.35)]">
            <div className="mt-3 grid grid-cols-3 gap-3">
              <div>
                <p className="text-xs text-[rgba(var(--fg),.6)]">Total</p>
                <p className="font-semibold">{formatMoney(cobranza.totalOriginal ?? factura.total)}</p>
              </div>
              <div>
                <p className="text-xs text-[rgba(var(--fg),.6)]">Pagado</p>
                <p className="font-semibold">{formatMoney(totalPagos)}</p>
              </div>
              <div>
                <p className="text-xs text-[rgba(var(--fg),.6)]">Saldo</p>
                <p className="text-lg font-bold text-[rgb(var(--brand))]">{formatMoney(saldo)}</p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <StatusBadge>{estadoCobro}</StatusBadge>
              <span className="text-[rgba(var(--fg),.62)]">
                {dueDate
                  ? `${estadoCobro === 'Vencida' ? 'Venció' : 'Vence'} el ${displayDate(dueDate)}`
                  : 'Sin vencimiento'}
              </span>
              {Number(cobranza.totalNotasCreditoAplicadas || 0) > 0 ? (
                <span>Notas de crédito: {formatMoney(cobranza.totalNotasCreditoAplicadas)}</span>
              ) : null}
            </div>
          </Panel>
        </section>

        <FiscalPanel className="order-1" summary={fiscal.cae ? `CAE autorizado · Total ${formatMoney(data?.impuestos?.total ?? factura.total)}` : null}>
          <div className="grid gap-5 xl:grid-cols-3">
            <div><h3 className="text-xs font-semibold uppercase tracking-wide text-[rgba(var(--muted),.92)]">Comprobante</h3>
            <DataList
              rows={[
                { label: 'Comprobante', value: typeLabel(fiscal) },
                { label: 'Número', value: documentNumber(factura, fiscal) },
                { label: 'Punto de venta', value: fiscal.puntoVenta == null ? 'Sin dato' : String(fiscal.puntoVenta).padStart(4, '0') },
                { label: 'Fecha comprobante', value: formatDate(fiscal.fechaComprobante) },
                { label: 'Condición de venta', value: fiscal.condicionVenta, visible: hasValue(fiscal.condicionVenta) },
                { label: 'Vencimiento', value: displayDate(dueDate), visible: hasValue(dueDate) },
                { label: 'Moneda', value: fiscal.moneda, visible: hasValue(fiscal.moneda) },
              ]}
            /></div>
            <div><h3 className="text-xs font-semibold uppercase tracking-wide text-[rgba(var(--muted),.92)]">Autorización fiscal</h3>
            <DataList
              rows={[
                { label: 'Estado', value: fiscal.resultadoAutorizacion, visible: hasValue(fiscal.resultadoAutorizacion) },
                {
                  label: 'CAE',
                  value: fiscal.cae ? (
                    <span className="inline-flex items-center gap-2">{fiscal.cae}<button type="button" aria-label="Copiar CAE" onClick={() => copyText(fiscal.cae, 'cae')}>{copied === 'cae' ? <Check size={15} /> : <Copy size={15} />}</button></span>
                  ) : null, visible: hasValue(fiscal.cae),
                },
                { label: 'Vencimiento CAE', value: formatDate(fiscal.caeVencimiento), visible: hasValue(fiscal.caeVencimiento) },
                { label: 'CUIT receptor', value: formatCuit(cliente.cuit), visible: hasValue(cliente.cuit) },
              ]}
            /></div>
            <div><h3 className="text-xs font-semibold uppercase tracking-wide text-[rgba(var(--muted),.92)]">Composición impositiva</h3>
            <DataList
              rows={[
                ...taxRows.map(([label, value]) => ({ label, value: formatMoney(value) })),
                { label: 'Total final', value: formatMoney(data?.impuestos?.total ?? factura.total) },
              ]}
            /></div>
          </div>
        </FiscalPanel>

        <Panel title="Detalle facturado">
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs text-[rgba(var(--fg),.6)]">
                <tr>
                  <th className="px-2 py-2">Descripción</th>
                  <th className="px-2 py-2 text-right">Cant.</th>
                  <th className="px-2 py-2 text-right">Precio</th>
                  <th className="px-2 py-2 text-right">IVA</th>
                  <th className="px-2 py-2 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {data.detalle?.map((row) => (
                  <tr key={row.id} className="border-t border-[rgba(var(--border),.42)]">
                    <td className="px-2 py-2 font-medium">{row.concept_name || row.descripcion}</td>
                    <td className="px-2 py-2 text-right">{row.cantidad}</td>
                    <td className="px-2 py-2 text-right">{formatMoney(row.precio_unitario)}</td>
                    <td className="px-2 py-2 text-right">{row.alicuota_iva ? `${row.alicuota_iva} %` : '—'}</td>
                    <td className="px-2 py-2 text-right font-semibold">{formatMoney(row.total)}</td>
                  </tr>
                ))}
                {redondeoFiscal ? (
                  <tr className="border-t border-dashed border-[rgba(var(--brand),.5)] bg-[rgba(var(--brand),.06)]">
                    <td className="px-2 py-2 font-medium">Ajuste por redondeo</td>
                    <td className="px-2 py-2 text-right">1</td>
                    <td className="px-2 py-2 text-right">{formatMoney(redondeoFiscal.baseAdjustment)}</td>
                    <td className="px-2 py-2 text-right">{redondeoFiscal.rate ? `${redondeoFiscal.rate} %` : '—'}</td>
                    <td className="px-2 py-2 text-right font-semibold">{formatMoney(redondeoFiscal.totalAdjustment)}</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </Panel>

        {!isDraft && (cobranza.cobranzas?.length || cobranza.notasCredito?.length) ? (
          <Panel title="Aplicaciones">
            <DataList
              rows={[
                { label: 'Pagos aplicados', value: formatMoney(totalPagos) },
                { label: 'Notas de crédito aplicadas', value: formatMoney(cobranza.totalNotasCreditoAplicadas) },
              ]}
            />
            {cobranza.cobranzas?.length ? <div className="mt-4 overflow-x-auto rounded-xl border border-[rgba(var(--border),.55)]"><table className="min-w-full text-sm"><thead className="bg-[rgba(var(--subpanel),.7)] text-left text-xs text-[rgba(var(--fg),.62)]"><tr><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Recibo</th><th className="px-3 py-2">Medios</th><th className="px-3 py-2 text-right">Aplicado</th><th className="px-3 py-2" /></tr></thead><tbody>{cobranza.cobranzas.map((item) => <tr key={`${item.id}-${item.recibo_id || ''}`} className="border-t border-[rgba(var(--border),.42)]"><td className="whitespace-nowrap px-3 py-2">{formatDate(item.fecha, true)}</td><td className="whitespace-nowrap px-3 py-2 font-medium">{item.numero_recibo || 'Cobranza registrada'}</td><td className="px-3 py-2 text-[rgba(var(--muted),.95)]">{item.medio || '—'}{item.minuta_numero ? ` · Minuta #${item.minuta_numero}` : ''}</td><td className="whitespace-nowrap px-3 py-2 text-right font-medium">{formatMoney(item.importe)}</td><td className="px-3 py-2 text-right">{item.recibo_id ? <Button type="button" variant="outline" className="h-8 px-2 text-xs" onClick={() => navigate(`/tesoreria/recibos/${item.recibo_id}`)}>Ver recibo</Button> : null}</td></tr>)}</tbody></table></div> : null}
          </Panel>
        ) : null}

        <details
          ref={historyRef}
          className="rounded-xl border border-[rgba(var(--border),.55)] bg-[rgba(var(--panel),.92)] px-4 py-3"
        >
          <summary className="cursor-pointer rounded-md text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]">
            Auditoría
          </summary>
          <DataList
            rows={[
              { label: 'Creada por', value: data.auditoria?.creadoPor?.nombre || factura.usuario || 'Sin dato' },
              { label: 'Creación', value: formatDate(data.auditoria?.fechaCreacion, true) },
              { label: 'Última modificación', value: formatDate(data.auditoria?.ultimaModificacion, true) },
              { label: 'Identificador', value: factura.id },
            ]}
          />
        </details>
      </main>
    </div>
  );
}
